import { getExactGoogleReviewCount } from "@/lib/company-reviews-count";
import CardAIStream from "@/components/card-ai-stream";
import { FadeInSection, StaggerContainer } from "@/components/company/company-animations";
import CardCompanyEvents from "@/components/company/card-company-events";
import CardEmployeeReviews from "@/components/company/card-employee-reviews";
import CardGoogleReviews from "@/components/company/card-google-reviews";
import CompanyTabReadFailed from "@/components/company/company-tab-read-failed";
import CompanySectionCard, {
  CompanyCardSkeleton,
} from "@/components/company/company-section-card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  isAnySectionCutByBudget,
  isAnySectionFailed,
  isAnySectionPending,
  useCompanySections,
} from "@/contexts/company-sections-context";
import {
  COMPANY_CARD_SECTIONS,
  COMPANY_TAB_SECTIONS,
} from "@/lib/company-card-sections";
import {
  hasEmployeeReviewsBreakdownData,
  hasGoogleReviewTopicsData,
} from "@/lib/company-data-availability";
import { hasCompanyEventsData } from "@/lib/company-events";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { FacetedFilterOption } from "@/components/button-faceted-filter";
import { useCompanyNewsData } from "@/hooks/useCompanyNewsData";
import {
  getReviewPlatformIcon,
  getPlatformBrandColor,
} from "@/lib/platform-brand-colors";
import { useTranslation } from "@/hooks/useTranslation";
import { StarIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import {
  BriefcaseIcon,
  GaugeIcon,
  NewspaperIcon,
  Share2Icon,
  SmileIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SiGlassdoor, SiIndeed } from "react-icons/si";
import { useCompanyTabContext } from "../company-tab-context";
import { NewsModal } from "./news-modal";
import { ModalDomain, NewsModalSources } from "./news-modal-sources";
import { NewsStatCard } from "./news-stat-card";

/** The sections the reads of this tab return the data of. */
const NEWS_READ_SECTIONS = [
  ...COMPANY_CARD_SECTIONS.posts,
  ...COMPANY_CARD_SECTIONS.reviews,
  ...COMPANY_CARD_SECTIONS.jobs,
];

/** A stat card's headline score out of five, with what it is based on. */
function ScoreOutOfFive({
  score,
  caption,
}: {
  score: number;
  caption: string;
}) {
  const { currentLanguage } = useTranslation();
  const formattedScore = new Intl.NumberFormat(currentLanguage, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(score);
  return (
    <>
      <div className="text-3xl font-bold">
        {formattedScore}
        <span className="text-lg text-muted-foreground font-normal">/5</span>
      </div>
      <div className="text-sm text-muted-foreground">{caption}</div>
    </>
  );
}

export default function CompanyNewsContent() {
  const { company } = useCompanyTabContext();
  const { t } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();
  const sections = useCompanySections();
  const isSocialPending = isAnySectionPending(sections, COMPANY_CARD_SECTIONS.posts);
  const isReviewsPending = isAnySectionPending(sections, COMPANY_CARD_SECTIONS.reviews);
  const isJobsPending = isAnySectionPending(sections, COMPANY_CARD_SECTIONS.jobs);
  const isPressPending = isAnySectionPending(sections, COMPANY_CARD_SECTIONS.press);
  const isEmployeeReviewsPending = isAnySectionPending(
    sections,
    COMPANY_CARD_SECTIONS.employeeReviews,
  );
  const isNewsCutByBudget = isAnySectionCutByBudget(sections, [
    ...NEWS_READ_SECTIONS,
    ...COMPANY_CARD_SECTIONS.press,
  ]);
  const isNewsFailed = isAnySectionFailed(sections, COMPANY_TAB_SECTIONS.news);

  const {
    posts,
    isLoadingSocials,
    socialPlatformsOptions,
    socialPlatformsSelected,
    setSocialPlatformsSelected,
    reviews,
    isLoadingReviews,
    reviewPlatformsOptions,
    reviewPlatformsSelected,
    setReviewPlatformsSelected,
    jobOffers,
    isLoadingJobs,
    jobPlatformsOptions,
    jobPlatformsSelected,
    setJobPlatformsSelected,
    refusal,
  } = useCompanyNewsData(
    company.id,
    company,
    !isAnySectionPending(sections, NEWS_READ_SECTIONS),
  );

  const [openModal, setOpenModal] = useState<ModalDomain | null>(null);

  const scrollLockRef = useRef<string>("");
  useEffect(() => {
    if (openModal) {
      scrollLockRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = scrollLockRef.current;
    }
    return () => {
      document.body.style.overflow = scrollLockRef.current;
    };
  }, [openModal]);

  const pressArticles = company.news?.articles ?? [];
  const pressCount = pressArticles.length;
  const hasPressArticles = pressCount > 0;
  const employeeReviewsCount = company.employeeReviews?.count ?? 0;
  const employeeReviewsScore = company.employeeReviews?.aggregateScore;
  const hasEmployeeReviews =
    employeeReviewsCount > 0 ||
    (employeeReviewsScore !== null && employeeReviewsScore !== undefined);

  // The global summary reads posts, reviews and job offers only: with none of
  // them the API answers 404 (COMPANY_NEWS_NOT_FOUND), so it is not requested.
  const isLoadingNewsSources = isLoadingSocials || isLoadingReviews || isLoadingJobs;
  const hasNewsSources =
    posts.length > 0 || reviews.length > 0 || jobOffers.length > 0;

  const googleRating = company.reviews?.google?.rating;
  const hasGoogleAggregateRating = !!googleRating;
  const hasReviewsToShow = reviews.length > 0 || hasGoogleAggregateRating;

  const reviewsCardPlatformOptions: FacetedFilterOption[] = [
    ...reviewPlatformsOptions,
  ];
  if (
    hasGoogleAggregateRating &&
    !reviewsCardPlatformOptions.some((o) => o.value === "google")
  ) {
    reviewsCardPlatformOptions.push({
      label: "Google",
      value: "google",
      icon: getReviewPlatformIcon("google"),
      iconClassName: getPlatformBrandColor("google"),
      total: getExactGoogleReviewCount(company.reviews?.google) ?? undefined,
    });
  }

  const employeeReviewPlatformsOptions: FacetedFilterOption[] = [
    {
      label: "Glassdoor",
      value: "glassdoor",
      icon: SiGlassdoor,
      iconClassName: getPlatformBrandColor("glassdoor"),
      total: 1,
    },
    {
      label: "Indeed",
      value: "indeed",
      icon: SiIndeed,
      iconClassName: getPlatformBrandColor("indeed"),
      total: 1,
    },
  ];

  // A card shows once it has data, or while its read or its section runs —
  // the same rule `CompanySectionCard` applies below, counted for the grid.
  const isPostsShown = posts.length > 0 || isLoadingSocials || isSocialPending;
  const isReviewsShown = hasReviewsToShow || isLoadingReviews || isReviewsPending;
  const isJobsShown = jobOffers.length > 0 || isLoadingJobs || isJobsPending;
  const isPressShown = hasPressArticles || isPressPending;
  const isEmployeeReviewsShown = hasEmployeeReviews || isEmployeeReviewsPending;
  const visibleStatsCount = [
    isPostsShown,
    isReviewsShown,
    isJobsShown,
    isPressShown,
    isEmployeeReviewsShown,
  ].filter(Boolean).length;
  const hasEventsData = hasCompanyEventsData(company, arePeopleVisible);
  const isEventsShown =
    hasEventsData ||
    isAnySectionPending(sections, COMPANY_CARD_SECTIONS.events);
  // The stat cards above give the scores and counts: the cards below add
  // only what they lack.
  const hasEmployeeReviewsDetail = hasEmployeeReviewsBreakdownData(company);
  const hasGoogleReviewsDetail = hasGoogleReviewTopicsData(company);
  const isReviewsDetailShown =
    hasEmployeeReviewsDetail ||
    hasGoogleReviewsDetail ||
    isEmployeeReviewsPending ||
    isAnySectionPending(sections, COMPANY_CARD_SECTIONS.googleReviews);

  const refusalAlert = refusal && (
    <Alert variant="warning">
      <GaugeIcon />
      <AlertDescription className="text-current">{t(refusal)}</AlertDescription>
    </Alert>
  );

  if (visibleStatsCount === 0 && !isEventsShown && !isReviewsDetailShown) {
    // A refused read, sections the spent budget never ran (the page's
    // banner says so) or that failed, are not "nothing found".
    if (refusalAlert) return refusalAlert;
    if (isNewsCutByBudget) return null;
    if (isNewsFailed) return <CompanyTabReadFailed />;
    return (
      <div className="mt-[100px]">
        <div className="text-center">{t("company.news.noDataFound")}</div>
        <div className="text-center text-gray-500">
          {t("company.news.contributeMessage")}
        </div>
      </div>
    );
  }

  const getModalConfig = (domain: ModalDomain) => {
    switch (domain) {
      case "socials":
        return {
          title: t("company.news.socialNetworks"),
          icon: Share2Icon,
          streamUrl: `/company/${company.id}/social-networks/activities/resume`,
          launchFetch: !isLoadingSocials,
          hasAiSummary: true,
        };
      case "reviews":
        return {
          title: t("company.news.reviews"),
          icon: StarIcon,
          streamUrl: `/company/${company.id}/reviews/resume`,
          launchFetch: !isLoadingReviews && reviews.length > 0,
          hasAiSummary: reviews.length > 0,
        };
      case "jobs":
        return {
          title: t("company.news.jobOffers"),
          icon: BriefcaseIcon,
          streamUrl: `/company/${company.id}/job-offers/resume`,
          launchFetch: !isLoadingJobs,
          hasAiSummary: true,
        };
      case "articles":
        return {
          title: t("company.news.pressArticles"),
          icon: NewspaperIcon,
          streamUrl: `/company/${company.id}/news/articles/resume`,
          launchFetch: pressArticles.length > 0,
          hasAiSummary: true,
        };
      case "employee-reviews":
        return {
          title: t("company.news.employeeReviews"),
          icon: SmileIcon,
          streamUrl: "",
          launchFetch: false,
          hasAiSummary: false,
        };
    }
  };

  const reviewsBadgeCount =
    reviews.length > 0
      ? reviews.length
      : getExactGoogleReviewCount(company.reviews?.google);

  const badgeCountByDomain: Record<ModalDomain, number | null> = {
    socials: posts.length,
    reviews: reviewsBadgeCount,
    jobs: jobOffers.length,
    articles: pressArticles.length,
    "employee-reviews": employeeReviewsCount,
  };

  const postsCard = (isLoading: boolean) => (
    <NewsStatCard
      icon={Share2Icon}
      count={posts.length}
      label={
        posts.length === 0
          ? t("company.news.noPublications")
          : t("company.news.publications", { count: posts.length })
      }
      isLoading={isLoading}
      platformOptions={socialPlatformsOptions}
      onClick={posts.length > 0 ? () => setOpenModal("socials") : undefined}
    />
  );

  const reviewsCard = (isLoading: boolean) => (
    <NewsStatCard
      icon={StarIcon}
      count={reviews.length}
      label={
        reviews.length === 0
          ? t("company.news.noReviews")
          : t("company.reviews.reviews")
      }
      isLoading={isLoading}
      platformOptions={reviewsCardPlatformOptions}
      onClick={hasReviewsToShow ? () => setOpenModal("reviews") : undefined}
      customContent={
        googleRating ? (
          <ScoreOutOfFive
            score={googleRating}
            caption={
              company.reviews.google.reviewCountRange
                ? t("company.reviews.basedOnRange", {
                    range: company.reviews.google.reviewCountRange,
                  })
                : t("company.reviews.basedOn", {
                    count: company.reviews.google.review_count || 0,
                  })
            }
          />
        ) : undefined
      }
    />
  );

  const jobsCard = (isLoading: boolean) => (
    <NewsStatCard
      icon={BriefcaseIcon}
      count={jobOffers.length}
      label={
        jobOffers.length === 0
          ? t("company.news.noOffers")
          : t("company.news.offers", { count: jobOffers.length })
      }
      isLoading={isLoading}
      platformOptions={jobPlatformsOptions}
      onClick={jobOffers.length > 0 ? () => setOpenModal("jobs") : undefined}
    />
  );

  const pressCard = (isLoading: boolean) => (
    <NewsStatCard
      icon={NewspaperIcon}
      count={pressCount}
      label={t("company.news.articles", { count: pressCount })}
      isLoading={isLoading}
      platformOptions={[]}
      onClick={hasPressArticles ? () => setOpenModal("articles") : undefined}
    />
  );

  const employeeReviewsCard = (isLoading: boolean) => (
    <NewsStatCard
      icon={SmileIcon}
      count={employeeReviewsCount}
      label={t("company.employeeReviews.title")}
      isLoading={isLoading}
      platformOptions={employeeReviewPlatformsOptions}
      onClick={
        hasEmployeeReviews ? () => setOpenModal("employee-reviews") : undefined
      }
      customContent={
        employeeReviewsScore != null ? (
          <ScoreOutOfFive
            score={employeeReviewsScore}
            caption={t("company.employeeReviews.basedOn", {
              count: employeeReviewsCount,
            })}
          />
        ) : undefined
      }
    />
  );

  return (
    <FadeInSection>
      <div className="@container space-y-4">
        {refusalAlert}
        {visibleStatsCount > 0 && (
          <div>
            <StaggerContainer
              className={cn(
                "grid grid-cols-1 gap-3 @md:grid-cols-2",
                visibleStatsCount === 5 && "@4xl:grid-cols-5",
                visibleStatsCount === 4 && "@4xl:grid-cols-4",
                visibleStatsCount === 3 && "@4xl:grid-cols-3",
              )}
            >
              {/* A read in flight shows its card's own loading state; a
                  section still running, its skeleton. */}
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.posts}
                hasData={posts.length > 0 || isLoadingSocials}
                skeleton={postsCard(true)}
              >
                {postsCard(isLoadingSocials)}
              </CompanySectionCard>
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.reviews}
                hasData={hasReviewsToShow || isLoadingReviews}
                skeleton={reviewsCard(true)}
              >
                {reviewsCard(isLoadingReviews)}
              </CompanySectionCard>
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.jobs}
                hasData={jobOffers.length > 0 || isLoadingJobs}
                skeleton={jobsCard(true)}
              >
                {jobsCard(isLoadingJobs)}
              </CompanySectionCard>
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.press}
                hasData={hasPressArticles}
                skeleton={pressCard(true)}
              >
                {pressCard(false)}
              </CompanySectionCard>
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.employeeReviews}
                hasData={hasEmployeeReviews}
                skeleton={employeeReviewsCard(true)}
              >
                {employeeReviewsCard(false)}
              </CompanySectionCard>
            </StaggerContainer>
          </div>
        )}

        {(isLoadingNewsSources || hasNewsSources) && (
          <CardAIStream
            title={t("company.news.globalSummary")}
            streamUrl={`/company/${company.id}/news/resume`}
            launchFetch={!isLoadingNewsSources && hasNewsSources}
          />
        )}

        {/* The detail behind the figures: the dated events, then what
            employees and customers say. Side by side when both have data. */}
        {(isEventsShown || isReviewsDetailShown) && (
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 basis-[420px] min-w-0 empty:hidden">
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.events}
                hasData={hasEventsData}
                skeleton={<CompanyCardSkeleton />}
              >
                <CardCompanyEvents company={company} />
              </CompanySectionCard>
            </div>
            <div className="flex flex-1 basis-[420px] min-w-0 flex-col gap-4 empty:hidden">
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.employeeReviews}
                hasData={hasEmployeeReviewsDetail}
                skeleton={<CompanyCardSkeleton />}
              >
                <CardEmployeeReviews company={company} withScore={false} />
              </CompanySectionCard>
              <CompanySectionCard
                sections={COMPANY_CARD_SECTIONS.googleReviews}
                hasData={hasGoogleReviewsDetail}
                skeleton={<CompanyCardSkeleton />}
              >
                <CardGoogleReviews company={company} withRating={false} />
              </CompanySectionCard>
            </div>
          </div>
        )}

        {openModal && (() => {
          const modalConfig = getModalConfig(openModal);
          return (
            <NewsModal
              title={modalConfig.title}
              icon={modalConfig.icon}
              badgeCount={badgeCountByDomain[openModal]}
              streamUrl={modalConfig.streamUrl}
              launchFetch={modalConfig.launchFetch}
              hasAiSummary={modalConfig.hasAiSummary}
              onClose={() => setOpenModal(null)}
              sourcesContent={
                <NewsModalSources
                  domain={openModal}
                  company={company}
                  posts={posts}
                  socialPlatformsOptions={socialPlatformsOptions}
                  socialPlatformsSelected={socialPlatformsSelected}
                  onSocialPlatformsChange={setSocialPlatformsSelected}
                  reviews={reviews}
                  reviewPlatformsOptions={reviewPlatformsOptions}
                  reviewPlatformsSelected={reviewPlatformsSelected}
                  onReviewPlatformsChange={setReviewPlatformsSelected}
                  jobOffers={jobOffers}
                  jobPlatformsOptions={jobPlatformsOptions}
                  jobPlatformsSelected={jobPlatformsSelected}
                  onJobPlatformsChange={setJobPlatformsSelected}
                  hasGoogleAggregateRating={hasGoogleAggregateRating}
                />
              }
            />
          );
        })()}
      </div>
    </FadeInSection>
  );
}
